import { useState, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  ClipboardDocumentIcon,
  CheckIcon,
  ArrowDownTrayIcon,
  EnvelopeIcon,
  DocumentMagnifyingGlassIcon,
  CloudArrowUpIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import {
  collectDiagnosticReport,
  type DiagnosticReport,
  type DiagnosticCategory,
} from "@/shared/services/diagnostic";
import { safeClipboardWrite } from "@/utils/clipboard";
import { submitDiagnosticReport } from "@/shared/api/pref-storage";
import { useIsPollingMode } from "@/shared/api/newsquawk-headlines";
import { useIsFirehosePollingMode } from "@/shared/api/firehose/useIsFirehosePollingMode";

const HEADLINES_SSE_ENDPOINT = `${import.meta.env.VITE_NOTIFICATIONS_BASE_URL}/api/stream`;
const FIREHOSE_SSE_ENDPOINT = `${import.meta.env.VITE_FIREHOSE_BASE_URL}/stream`;
const APP_DOMAIN = window.location.origin;

const DETAILS_TEXT = `Newsquawk — Please allow SSE streaming endpoints

Newsquawk delivers real-time financial headlines and stories via Server-Sent Events (SSE). Our portal has detected that these connections are being blocked or dropped by the network, causing it to fall back to periodic polling (up to 2–3 second delay instead of sub-second).

Please allowlist the following so SSE connections can stay open:

  Domain: ${APP_DOMAIN}
  Headlines SSE endpoint: ${HEADLINES_SSE_ENDPOINT}
  Firehose SSE endpoint: ${FIREHOSE_SSE_ENDPOINT}

Technical details:
  - Protocol: HTTPS (port 443)
  - Method: Standard HTTP GET with a long-lived response (text/event-stream)
  - No WebSocket upgrade or special ports required
  - The connection just needs to be allowed to remain open by proxies, firewalls, and SSL inspection appliances`;

const ALWAYS_INCLUDED_CATEGORIES: DiagnosticCategory[] = [
  "environment",
  "appLifecycle",
  "widgetState",
  "sentryReplay",
  "eventTimeline",
];

const ISSUE_OPTIONS: {
  id: string;
  label: string;
  description: string;
  categories: DiagnosticCategory[];
}[] = [
  {
    id: "audioIssue",
    label: "Audio not working or cutting out",
    description:
      "The squawk player won't connect, audio drops, or you hear no sound",
    categories: ["audioPlayer"],
  },
  {
    id: "headlinesIssue",
    label: "Headlines not updating",
    description:
      "Headlines appear delayed, stuck, or the feed shows a Polling indicator",
    categories: ["sseState", "networkErrors"],
  },
  {
    id: "otherIssue",
    label: "Other issue",
    description: "Something else not listed above",
    categories: ["sseState", "networkErrors", "audioPlayer"],
  },
];

function eventLevelColor(level: string): string {
  switch (level) {
    case "error":
      return "text-red-600 dark:text-red-400";
    case "warn":
      return "text-amber-600 dark:text-amber-400";
    default:
      return "text-gray-600 dark:text-gray-300";
  }
}

function downloadJSON(report: DiagnosticReport) {
  const blob = new Blob([JSON.stringify(report, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `newsquawk-diagnostic-${report.reportId}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

function buildSummaryText(report: DiagnosticReport): string {
  const lines: string[] = [
    `Newsquawk Diagnostic Report: ${report.reportId}`,
    `Generated: ${report.generatedAt}`,
    `App: ${report.appVersion} (${report.commitSha})`,
    "",
  ];

  if (report.categories.sseState?.length) {
    lines.push(`--- SSE Connection Events (${report.categories.sseState.length}) ---`);
    for (const e of report.categories.sseState.slice(-20)) {
      const details = e.details ? ` ${JSON.stringify(e.details)}` : "";
      lines.push(`  ${e.timestamp} [${e.level}] ${e.event}${details}`);
    }
    lines.push("");
  }

  if (report.categories.networkErrors?.length) {
    lines.push(
      `--- Network Errors (${report.categories.networkErrors.length}) ---`
    );
    for (const err of report.categories.networkErrors.slice(-10)) {
      lines.push(
        `  ${err.timestamp} [${err.source}] ${err.event}: ${err.details?.status ?? ""} ${err.details?.method ?? ""} ${err.details?.path ?? ""}`
      );
    }
    lines.push("");
  }

  if (report.categories.environment) {
    const env = report.categories.environment;
    lines.push("--- Environment ---");
    lines.push(`  Online: ${env.online}`);
    lines.push(`  Screen: ${env.screenWidth}x${env.screenHeight}`);
    lines.push(`  Uptime: ${Math.round(env.timeSincePageLoadMs / 1000)}s`);
    lines.push(`  Auth: ${env.keycloak.authenticated}`);
    lines.push("");
  }

  if (report.categories.sentryReplay) {
    lines.push("--- Sentry Replay ---");
    lines.push(
      report.categories.sentryReplay.replayActive
        ? `  Replay ID: ${report.categories.sentryReplay.replayId}`
        : "  No active replay"
    );
    lines.push("");
  }

  if (report.categories.appLifecycle?.length) {
    lines.push(`--- App Lifecycle (${report.categories.appLifecycle.length}) ---`);
    for (const e of report.categories.appLifecycle.slice(-20)) {
      const details = e.details ? ` ${JSON.stringify(e.details)}` : "";
      lines.push(`  ${e.timestamp} [${e.source.split(":")[0]}] ${e.event}${details}`);
    }
    lines.push("");
  }

  if (report.categories.widgetState?.length) {
    lines.push(`--- Widget State (${report.categories.widgetState.length}) ---`);
    for (const e of report.categories.widgetState.slice(-20)) {
      const details = e.details ? ` ${JSON.stringify(e.details)}` : "";
      lines.push(`  ${e.timestamp} ${e.event}${details}`);
    }
    lines.push("");
  }

  if (report.categories.audioPlayer?.length) {
    lines.push(`--- Audio Player (${report.categories.audioPlayer.length}) ---`);
    for (const e of report.categories.audioPlayer.slice(-20)) {
      const details = e.details ? ` ${JSON.stringify(e.details)}` : "";
      lines.push(`  ${e.timestamp} [${e.source.split(":")[0]}] ${e.event}${details}`);
    }
    lines.push("");
  }

  if (report.throughput?.length) {
    lines.push("--- Throughput (msgs/min, recent→old) ---");
    for (const t of report.throughput) {
      lines.push(`  ${t.source}: [${t.buckets.join(", ")}]`);
    }
    lines.push("");
  }

  return lines.join("\n");
}

function NetworkRequirementsBlock() {
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
        Network requirements
      </h3>
      <div className="mt-3 space-y-2">
        <div className="rounded-md bg-gray-100 dark:bg-gray-700/50 px-4 py-3">
          <span className="text-2xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Domain
          </span>
          <code className="block text-xs font-mono text-gray-800 dark:text-gray-200 break-all select-all mt-1">
            {APP_DOMAIN}
          </code>
        </div>
        <div className="rounded-md bg-gray-100 dark:bg-gray-700/50 px-4 py-3">
          <span className="text-2xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Headlines SSE Endpoint
          </span>
          <code className="block text-xs font-mono text-gray-800 dark:text-gray-200 break-all select-all mt-1">
            {HEADLINES_SSE_ENDPOINT}
          </code>
        </div>
        <div className="rounded-md bg-gray-100 dark:bg-gray-700/50 px-4 py-3">
          <span className="text-2xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Firehose SSE Endpoint
          </span>
          <code className="block text-xs font-mono text-gray-800 dark:text-gray-200 break-all select-all mt-1">
            {FIREHOSE_SSE_ENDPOINT}
          </code>
        </div>
      </div>
    </div>
  );
}

function ConnectionDetailsBlock() {
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
        Connection details
      </h3>
      <ul className="mt-2 space-y-1 text-sm text-gray-600 dark:text-gray-300 list-disc list-inside">
        <li>HTTPS (port 443)</li>
        <li>
          Standard HTTP GET with a long-lived response (text/event-stream)
        </li>
        <li>No WebSocket upgrade or special ports required</li>
        <li>
          The connection just needs to be allowed to remain open by proxies,
          firewalls, and SSL inspection appliances
        </li>
      </ul>
    </div>
  );
}

function SseStatusPill({ polling }: { polling: boolean }) {
  if (polling) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-200">
        <span className="size-1.5 rounded-full bg-amber-500" />
        Polling fallback
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 dark:bg-green-900/30 px-2 py-0.5 text-xs font-medium text-green-800 dark:text-green-300">
      <span className="size-1.5 rounded-full bg-green-500" />
      Connected
    </span>
  );
}

function Troubleshooting() {
  const isHeadlinesPolling = useIsPollingMode();
  const isFirehosePolling = useIsFirehosePollingMode();
  const isAnyPolling = isHeadlinesPolling || isFirehosePolling;
  const [copied, setCopied] = useState(false);
  const [requirementsExpanded, setRequirementsExpanded] = useState(false);
  const [selectedIssues, setSelectedIssues] = useState<Set<string>>(
    () => new Set(ISSUE_OPTIONS.map((o) => o.id))
  );
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState<DiagnosticReport | null>(null);
  const [summaryCopied, setSummaryCopied] = useState(false);

  const uploadMutation = useMutation({
    mutationFn: (r: DiagnosticReport) => submitDiagnosticReport(r),
  });

  const copyDetails = async () => {
    await safeClipboardWrite(DETAILS_TEXT);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleIssue = (id: string) => {
    setSelectedIssues((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const generateReport = useCallback(async () => {
    setGenerating(true);
    setReport(null);
    uploadMutation.reset();
    try {
      const categories = new Set<DiagnosticCategory>(ALWAYS_INCLUDED_CATEGORIES);
      for (const issue of ISSUE_OPTIONS) {
        if (selectedIssues.has(issue.id)) {
          for (const cat of issue.categories) {
            categories.add(cat);
          }
        }
      }
      const r = await collectDiagnosticReport(categories);
      setReport(r);
    } catch (err) {
      console.error("Failed to generate diagnostic report:", err);
    } finally {
      setGenerating(false);
    }
  }, [selectedIssues]);

  const copySummary = async () => {
    if (!report) return;
    await safeClipboardWrite(buildSummaryText(report));
    setSummaryCopied(true);
    setTimeout(() => setSummaryCopied(false), 2000);
  };

  return (
    <div className="divide-y divide-gray-200 dark:divide-white/10">
      {/* SSE Connectivity */}
      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">
              Live Streaming (SSE)
            </h2>
            <SseStatusPill polling={isAnyPolling} />
          </div>
          <p className="mt-1 text-sm/6 text-gray-500 dark:text-gray-400">
            Headlines and stories stream in real time over Server-Sent Events.
          </p>
        </div>
        <div className="md:col-span-2">
          {isAnyPolling ? (
            <div className="space-y-6">
              <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-4">
                <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                  Live streaming is unavailable on your network
                </h3>
                <p className="mt-2 text-sm text-amber-800 dark:text-amber-200">
                  SSE connections are being blocked, so we&apos;ve switched to
                  periodic polling. Stories arrive with a 2–3 second delay
                  instead of sub-second. Send the network requirements below
                  to your IT team to restore live streaming.
                </p>
                <div className="mt-3">
                  <button
                    onClick={copyDetails}
                    className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 dark:bg-amber-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 dark:hover:bg-amber-400 transition-colors cursor-pointer"
                  >
                    {copied ? (
                      <>
                        <CheckIcon className="size-3.5" />
                        Copied
                      </>
                    ) : (
                      <>
                        <ClipboardDocumentIcon className="size-3.5" />
                        Copy for IT team
                      </>
                    )}
                  </button>
                </div>
              </div>

              <NetworkRequirementsBlock />
              <ConnectionDetailsBlock />
            </div>
          ) : (
            <div>
              <button
                type="button"
                onClick={() => setRequirementsExpanded((v) => !v)}
                className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-squawk-blue dark:hover:text-squawk-blue cursor-pointer transition-colors"
              >
                <ChevronRightIcon
                  className={`size-4 transition-transform ${
                    requirementsExpanded ? "rotate-90" : ""
                  }`}
                />
                Having issues? View network requirements for IT
              </button>
              {requirementsExpanded && (
                <div className="mt-6 space-y-6">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    If a widget header shows a Polling indicator, your network
                    may be blocking SSE connections. Allowlisting the
                    following typically restores live streaming.
                  </p>
                  <NetworkRequirementsBlock />
                  <ConnectionDetailsBlock />
                  <div>
                    <button
                      onClick={copyDetails}
                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:border-squawk-blue hover:text-squawk-blue dark:hover:text-squawk-blue transition-colors cursor-pointer"
                    >
                      {copied ? (
                        <>
                          <CheckIcon className="size-4" />
                          Copied
                        </>
                      ) : (
                        <>
                          <ClipboardDocumentIcon className="size-4" />
                          Copy for IT team
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Diagnostic Report */}
      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">
            Diagnostic Report
          </h2>
          <p className="mt-1 text-sm/6 text-gray-500 dark:text-gray-400">
            Tell us what&apos;s wrong and we&apos;ll collect the right
            diagnostic information to help support investigate.
          </p>
        </div>
        <div className="md:col-span-2 space-y-6">
          {/* Issue checkboxes */}
          <fieldset>
            <div className="flex items-center justify-between">
              <legend className="text-sm font-semibold text-gray-900 dark:text-white">
                What issue are you experiencing?
              </legend>
              <button
                type="button"
                onClick={() =>
                  setSelectedIssues((prev) =>
                    prev.size === ISSUE_OPTIONS.length
                      ? new Set()
                      : new Set(ISSUE_OPTIONS.map((o) => o.id))
                  )
                }
                className="text-xs text-squawk-blue hover:text-squawk-blue/80 cursor-pointer"
              >
                {selectedIssues.size === ISSUE_OPTIONS.length
                  ? "Unselect all"
                  : "Select all"}
              </button>
            </div>
            <div className="mt-3 space-y-3">
              {ISSUE_OPTIONS.map((issue) => (
                <label
                  key={issue.id}
                  className="flex items-start gap-3 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedIssues.has(issue.id)}
                    onChange={() => toggleIssue(issue.id)}
                    className="mt-0.5 size-4 rounded border-gray-300 dark:border-gray-600 text-squawk-blue focus:ring-squawk-blue cursor-pointer"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {issue.label}
                    </span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {issue.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </fieldset>

          {/* Generate button */}
          <button
            onClick={generateReport}
            disabled={generating}
            className="inline-flex items-center gap-2 rounded-md bg-squawk-blue px-4 py-2 text-sm font-medium text-white hover:bg-squawk-blue/80 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DocumentMagnifyingGlassIcon className="size-4" />
            {generating ? "Generating..." : "Generate Diagnostic Report"}
          </button>

          {/* Report preview + actions */}
          {report && (
            <div className="space-y-4 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Report: {report.reportId}
                </h3>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {new Date(report.generatedAt).toLocaleString()}
                </span>
              </div>

              {/* SSE state preview */}
              {report.categories.sseState &&
                report.categories.sseState.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      SSE Connection Events ({report.categories.sseState.length})
                    </h4>
                    <div className="max-h-40 overflow-y-auto rounded-md bg-gray-50 dark:bg-gray-800 p-2 space-y-1">
                      {report.categories.sseState.slice(-20).map((e, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 text-xs font-mono"
                        >
                          <span className="text-gray-400 dark:text-gray-500 shrink-0">
                            {new Date(e.timestamp).toLocaleTimeString()}
                          </span>
                          <span className={eventLevelColor(e.level)}>
                            {e.event}
                          </span>
                          {e.details && (
                            <span className="text-gray-400 dark:text-gray-500 truncate">
                              {JSON.stringify(e.details)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Event timeline preview */}
              {report.categories.eventTimeline &&
                report.categories.eventTimeline.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      Recent Events ({report.categories.eventTimeline.length})
                    </h4>
                    <div className="max-h-40 overflow-y-auto rounded-md bg-gray-50 dark:bg-gray-800 p-2 space-y-1">
                      {report.categories.eventTimeline.slice(-20).map((e, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 text-xs font-mono"
                        >
                          <span className="text-gray-400 dark:text-gray-500 shrink-0">
                            {new Date(e.timestamp).toLocaleTimeString()}
                          </span>
                          <span className={eventLevelColor(e.level)}>
                            [{e.source}] {e.event}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Network errors count */}
              {report.categories.networkErrors && (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Network Errors:
                  </span>
                  <span
                    className={`text-sm font-medium ${
                      report.categories.networkErrors.length > 0
                        ? "text-red-600 dark:text-red-400"
                        : "text-green-600 dark:text-green-400"
                    }`}
                  >
                    {report.categories.networkErrors.length}
                  </span>
                </div>
              )}

              {/* Throughput preview */}
              {report.throughput && report.throughput.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                    Message Throughput (msgs/min)
                  </h4>
                  <div className="space-y-1">
                    {report.throughput.map((t) => (
                      <div
                        key={t.source}
                        className="flex items-center gap-2 text-xs"
                      >
                        <span className="text-gray-600 dark:text-gray-300 font-medium w-32 truncate">
                          {t.source}
                        </span>
                        <div className="flex gap-1">
                          {t.buckets.map((count, i) => (
                            <span
                              key={i}
                              className={`inline-block w-6 text-center rounded px-1 ${
                                count > 0
                                  ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                  : "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500"
                              }`}
                            >
                              {count}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Widget state preview */}
              {report.categories.widgetState &&
                report.categories.widgetState.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      Widget State Events ({report.categories.widgetState.length})
                    </h4>
                    <div className="max-h-40 overflow-y-auto rounded-md bg-gray-50 dark:bg-gray-800 p-2 space-y-1">
                      {report.categories.widgetState.slice(-20).map((e, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 text-xs font-mono"
                        >
                          <span className="text-gray-400 dark:text-gray-500 shrink-0">
                            {new Date(e.timestamp).toLocaleTimeString()}
                          </span>
                          <span className={eventLevelColor(e.level)}>
                            {e.event}
                          </span>
                          {e.details && (
                            <span className="text-gray-400 dark:text-gray-500 truncate">
                              {JSON.stringify(e.details)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* App lifecycle preview */}
              {report.categories.appLifecycle &&
                report.categories.appLifecycle.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      App Lifecycle ({report.categories.appLifecycle.length})
                    </h4>
                    <div className="max-h-40 overflow-y-auto rounded-md bg-gray-50 dark:bg-gray-800 p-2 space-y-1">
                      {report.categories.appLifecycle.slice(-20).map((e, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 text-xs font-mono"
                        >
                          <span className="text-gray-400 dark:text-gray-500 shrink-0">
                            {new Date(e.timestamp).toLocaleTimeString()}
                          </span>
                          <span className={eventLevelColor(e.level)}>
                            [{e.source.split(":")[0]}] {e.event}
                          </span>
                          {e.details && (
                            <span className="text-gray-400 dark:text-gray-500 truncate">
                              {JSON.stringify(e.details)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Audio player preview */}
              {report.categories.audioPlayer &&
                report.categories.audioPlayer.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      Audio Player Events ({report.categories.audioPlayer.length})
                    </h4>
                    <div className="max-h-40 overflow-y-auto rounded-md bg-gray-50 dark:bg-gray-800 p-2 space-y-1">
                      {report.categories.audioPlayer.slice(-20).map((e, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 text-xs font-mono"
                        >
                          <span className="text-gray-400 dark:text-gray-500 shrink-0">
                            {new Date(e.timestamp).toLocaleTimeString()}
                          </span>
                          <span className={eventLevelColor(e.level)}>
                            [{e.source.split(":")[0]}] {e.event}
                          </span>
                          {e.details && (
                            <span className="text-gray-400 dark:text-gray-500 truncate">
                              {JSON.stringify(e.details)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Sentry replay */}
              {report.categories.sentryReplay && (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Sentry Replay:
                  </span>
                  {report.categories.sentryReplay.replayActive ? (
                    <span className="text-xs text-green-600 dark:text-green-400 font-mono">
                      {report.categories.sentryReplay.replayId}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      No active replay
                    </span>
                  )}
                </div>
              )}

              {/* Delivery actions */}
              <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => uploadMutation.mutate(report)}
                  disabled={uploadMutation.isPending || uploadMutation.isSuccess}
                  className="inline-flex items-center gap-1.5 rounded-md bg-squawk-blue px-4 py-2 text-sm font-medium text-white hover:bg-squawk-blue/80 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadMutation.isPending ? (
                    <>Sending...</>
                  ) : uploadMutation.isSuccess ? (
                    <>
                      <CheckIcon className="size-4" />
                      Sent
                    </>
                  ) : (
                    <>
                      <CloudArrowUpIcon className="size-4" />
                      Send to Support
                    </>
                  )}
                </button>
                {uploadMutation.isError && (
                  <span className="text-xs text-red-600 dark:text-red-400">
                    Failed to send — try downloading instead
                  </span>
                )}
                <button
                  onClick={() => downloadJSON(report)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:border-squawk-blue hover:text-squawk-blue dark:hover:text-squawk-blue transition-colors cursor-pointer"
                >
                  <ArrowDownTrayIcon className="size-4" />
                  Download JSON
                </button>
                <button
                  onClick={copySummary}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:border-squawk-blue hover:text-squawk-blue dark:hover:text-squawk-blue transition-colors cursor-pointer"
                >
                  {summaryCopied ? (
                    <>
                      <CheckIcon className="size-4" />
                      Copied
                    </>
                  ) : (
                    <>
                      <ClipboardDocumentIcon className="size-4" />
                      Copy Summary
                    </>
                  )}
                </button>
                <a
                  href={`mailto:support@newsquawk.com?subject=Diagnostic Report ${report.reportId}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:border-squawk-blue hover:text-squawk-blue dark:hover:text-squawk-blue transition-colors"
                >
                  <EnvelopeIcon className="size-4" />
                  Email Support
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Contact Support */}
      <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
        <div>
          <h2 className="text-base/7 font-semibold text-gray-900 dark:text-white">
            Contact Support
          </h2>
          <p className="mt-1 text-sm/6 text-gray-500 dark:text-gray-400">
            If the issue persists after allowlisting the endpoint, our support
            team can help diagnose the problem.
          </p>
        </div>
        <div className="self-center">
          <a
            href="mailto:support@newsquawk.com"
            className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:border-squawk-blue hover:text-squawk-blue dark:hover:text-squawk-blue transition-colors"
          >
            Email support@newsquawk.com
          </a>
        </div>
      </div>
    </div>
  );
}

export default Troubleshooting;
